import React, { useState, useEffect } from 'react';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if user is on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsIOS(isIOSDevice);
    
    // Check if app is already installed (running in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
    
    // Check if user dismissed the prompt in last 48 hours
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    const now = new Date().getTime();
    const fortyEightHours = 48 * 60 * 60 * 1000;
    const shouldShowPrompt = !dismissedTime || (now - parseInt(dismissedTime)) > fortyEightHours;
    

    
    if (isMobile && !isStandalone && shouldShowPrompt) {
      // Listen for the beforeinstallprompt event
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
        
        // Show with animation immediately
        setTimeout(() => {
          setIsVisible(true);
        }, 500);
      };
      
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      // For iOS devices (which don't support beforeinstallprompt)
      if (isIOSDevice) {
        setShowPrompt(true);
        setTimeout(() => {
          setIsVisible(true);
        }, 500);
      }
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android Chrome and other browsers that support beforeinstallprompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
    
      }
      
      setDeferredPrompt(null);
      handleClose();
    } else {
      // iOS Safari and other browsers that don't support beforeinstallprompt
      // Show instructions instead of closing
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    // Store dismissal time in localStorage
    const now = new Date().getTime();
    localStorage.setItem('pwa-install-dismissed', now.toString());

    handleClose();
  };

  const handleClose = () => {
    setIsVisible(false);
    setShowInstructions(false);
    setTimeout(() => {
      setShowPrompt(false);
    }, 300);
  };

  const handleBackToPrompt = () => {
    setShowInstructions(false);
  };



  if (!showPrompt) return null;

  return (
    <>
      <div className={`pwa-overlay ${isVisible ? 'visible' : ''}`} onClick={handleClose} />
      <div className={`pwa-install-prompt ${isVisible ? 'visible' : ''}`}>
        <div className="pwa-content">
          {!showInstructions ? (
            <>
              <div className="pwa-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#4CAF50"/>
                  <path d="M12 16L13.09 22.26L20 23L13.09 23.74L12 30L10.91 23.74L4 23L10.91 22.26L12 16Z" fill="#4CAF50" opacity="0.7"/>
                </svg>
              </div>
              
              <div className="pwa-text">
                <h3>აპლიკაციის დაყენება</h3>
                <p>დააყენეთ NTF აპლიკაცია თქვენს მოწყობილობაზე სწრაფი წვდომისთვის და უკეთესი გამოცდილებისთვის</p>
              </div>
              
              <div className="pwa-actions">
                <button className="pwa-btn pwa-btn-primary" onClick={handleInstall}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15L7 10H10V3H14V10H17L12 15Z" fill="currentColor"/>
                    <path d="M20 18H4V20H20V18Z" fill="currentColor"/>
                  </svg>
                  {isIOS ? 'ინსტრუქციების ნახვა' : 'დაყენება'}
                </button>
                
                <button className="pwa-btn pwa-btn-secondary" onClick={handleDismiss}>
                  არა, მადლობა
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="pwa-instructions">
                <div className="pwa-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4CAF50"/>
                  </svg>
                </div>
                
                <div className="pwa-text">
                  <h3>როგორ დავაყენოთ აპლიკაცია</h3>
                  <div className="pwa-steps">
                    <div className="pwa-step">
                      <div className="pwa-step-number">1</div>
                      <div className="pwa-step-content">
                        <p>დააჭირეთ <strong>გაზიარების</strong> ღილაკს Safari-ს ქვედა ნაწილში</p>
                        <div className="pwa-step-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92S19.61 16.08 18 16.08z" fill="#007AFF"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pwa-step">
                      <div className="pwa-step-number">2</div>
                      <div className="pwa-step-content">
                        <p>აირჩიეთ <strong>"მთავარ ეკრანზე დამატება"</strong></p>
                        <div className="pwa-step-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#007AFF"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pwa-step">
                      <div className="pwa-step-number">3</div>
                      <div className="pwa-step-content">
                        <p>დააჭირეთ <strong>"დამატება"</strong> ღილაკს</p>
                        <div className="pwa-step-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#4CAF50"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pwa-actions">
                  <button className="pwa-btn pwa-btn-primary" onClick={handleClose}>
                    გასაგებია
                  </button>
                  
                  <button className="pwa-btn pwa-btn-secondary" onClick={handleBackToPrompt}>
                    უკან
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        
        <button className="pwa-close" onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </>
  );
};

// Export manual trigger for debugging
export const triggerPWAInstall = () => {
  const event = new CustomEvent('manual-pwa-trigger');
  window.dispatchEvent(event);
};

export default PWAInstallPrompt;